import { ServiceUsersSelectModal } from "@/pages/settings/roles/edit/widgets/ServiceUsersSelectModal.tsx";
import { Box, Card, SelectItem, Stack, Tag, Title, useModals } from "@skailer-com/uikit";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

type ServiceUserAssignProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  error?: string;
};

export function ServiceUserAssign<TFieldValues extends FieldValues>({
  control,
  name,
  error,
}: ServiceUserAssignProps<TFieldValues>) {
  const { t } = useTranslation();
  const { openModal } = useModals();

  return (
    <Stack direction={"vertical"} gap={"md"}>
      <Title
        header={t("core:assign_service_user")}
        caption={t("core:assign_service_user_description")}
        size={"lg"}
        paddingBottom
        paddingTop
      />

      <Controller
        control={control}
        name={name}
        render={({ field: { value, onChange } }) => {
          const selected: SelectItem[] = (value as SelectItem[] | undefined) ?? [];
          const openSelect = () =>
            openModal(ServiceUsersSelectModal, { selectedEntities: selected, onApply: onChange });

          return (
            <Stack direction={"vertical"} gap={"md"}>
              <Stack
                direction={"horizontal"}
                gap={"md"}
                className={"grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"}
              >
                {selected.length > 0 && (
                  <Card
                    header={t("core:service_users")}
                    caption={selected.map((entity) => entity.title).join(", ")}
                    onClick={openSelect}
                    buttonsSlot={
                      <Tag>{t("plurals:service_users_count", { count: selected.length })}</Tag>
                    }
                  />
                )}

                {selected.length === 0 && (
                  <Card
                    icon={"fa-plus"}
                    header={t("core:select_service_users")}
                    onClick={openSelect}
                    variant={"action"}
                  />
                )}
              </Stack>

              {error && (
                <Box className={"pt-2 pb-4"}>
                  <span className={"text-danger"}>{error}</span>
                </Box>
              )}
            </Stack>
          );
        }}
      />
    </Stack>
  );
}
